import React, { useEffect, useRef, useState, useLayoutEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import AppIcon from "../../components/Common/AppIcon";

const SidebarFlyoutSubmenu = ({
  activeFlyout,
  sidebarRect,
  onClose,
  onMouseEnterFlyout,
  onMouseLeaveFlyout,
  isCollapsed,
}) => {
  const panelRef = useRef(null);
  const location = useLocation();
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, maxHeight: 400 });
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const calculatePosition = useCallback(() => {
    if (!activeFlyout?.triggerRect || typeof window === "undefined") return;

    const sRight = sidebarRect ? sidebarRect.right : activeFlyout.triggerRect.right;
    const left = sRight + 14;
    const initialTop = activeFlyout.triggerRect.top - 8;

    const vh = window.innerHeight || 800;
    const maxAllowedHeight = Math.max(200, vh - 24);

    let panelHeight = 280;
    if (panelRef.current) {
      panelHeight = panelRef.current.offsetHeight || 280;
    }

    const minTop = 12;
    const maxTop = Math.max(minTop, vh - panelHeight - 12);
    const clampedTop = Math.max(minTop, Math.min(initialTop, maxTop));

    setPanelPos({
      left,
      top: clampedTop,
      maxHeight: maxAllowedHeight,
    });
  }, [activeFlyout, sidebarRect]);

  useLayoutEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  // Recalculate on window resize or scroll
  useEffect(() => {
    if (!activeFlyout) return;
    const handleRecalc = () => calculatePosition();
    window.addEventListener("resize", handleRecalc, { passive: true });
    window.addEventListener("scroll", handleRecalc, { passive: true, capture: true });
    return () => {
      window.removeEventListener("resize", handleRecalc);
      window.removeEventListener("scroll", handleRecalc, { capture: true });
    };
  }, [activeFlyout, calculatePosition]);

  // Handle outside click dismissal
  useEffect(() => {
    if (!activeFlyout) return;

    const handlePointerDown = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        !activeFlyout.triggerEl?.contains(e.target)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [activeFlyout, onClose]);

  // Keyboard navigation inside the flyout panel
  const handleKeyDown = (e) => {
    const items = panelRef.current?.querySelectorAll?.("[role='menuitem']") || [];
    if (!items.length) return;

    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      activeFlyout?.triggerEl?.focus?.();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = (focusedIndex + 1) % items.length;
      setFocusedIndex(nextIndex);
      items[nextIndex]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex = (focusedIndex - 1 + items.length) % items.length;
      setFocusedIndex(prevIndex);
      items[prevIndex]?.focus();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      onClose();
      activeFlyout?.triggerEl?.focus?.();
    }
  };

  if (!activeFlyout || !isCollapsed || typeof document === "undefined") {
    return null;
  }

  const { id, title, icon, items = [] } = activeFlyout;

  return ReactDOM.createPortal(
    <div
      ref={panelRef}
      id={`sidebar-flyout-${id}`}
      className="sidebar-flyout-panel"
      role="menu"
      aria-label={title || "Submenu"}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnterFlyout}
      onMouseLeave={onMouseLeaveFlyout}
      style={{
        position: "fixed",
        top: `${panelPos.top}px`,
        left: `${panelPos.left}px`,
        maxHeight: `${panelPos.maxHeight}px`,
        zIndex: 100060,
      }}
    >
      {title && (
        <div className="sidebar-flyout-header">
          {icon && (
            <span className="sidebar-flyout-header__icon" aria-hidden="true">
              {typeof icon === "string" ? <AppIcon name={icon} size={18} /> : icon}
            </span>
          )}
          <span className="sidebar-flyout-header__title">{title}</span>
        </div>
      )}

      <div className="sidebar-flyout-items">
        {items.map((item, idx) => {
          if (!item) return null;
          const { name, to, icon: itemIcon, isNavActive } = item;
          const currentPath = location.pathname || "";
          const isActive =
            typeof isNavActive === "function"
              ? isNavActive(currentPath)
              : to
                ? currentPath === `/${to}` || currentPath.startsWith(`/${to}/`)
                : false;

          return (
            <NavLink
              key={to || name || idx}
              to={to ? `/${to}` : "#"}
              role="menuitem"
              tabIndex={focusedIndex === idx ? 0 : -1}
              className={`sidebar-flyout-link ${isActive ? "active" : ""}`}
              onClick={() => {
                onClose();
              }}
            >
              {itemIcon && (
                <span className="sidebar-flyout-link__icon" aria-hidden="true">
                  {typeof itemIcon === "string" ? (
                    <AppIcon name={itemIcon} size={18} />
                  ) : (
                    itemIcon
                  )}
                </span>
              )}
              <span className="sidebar-flyout-link__label">{name}</span>
              {isActive && (
                <span className="sidebar-flyout-link__badge" aria-hidden="true" />
              )}
            </NavLink>
          );
        })}
      </div>
    </div>,
    document.body
  );
};

export default SidebarFlyoutSubmenu;

